import { faBan } from '@fortawesome/pro-regular-svg-icons/faBan'
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV'
import { faHistory } from '@fortawesome/pro-regular-svg-icons/faHistory'
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink'
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { ActionIcon, Box, Button, Flex, Menu } from '@mantine/core'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetInquiryById } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { useInquiryActions } from '../../hooks/useInquiryActions'

export type HeaderActionsProps = {
  inquiryId: number
}

export function HeaderActions({ inquiryId }: HeaderActionsProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'inquiries' })
  const { t: tCommon } = useTranslation('shared', { keyPrefix: 'actions' })
  const { isMobile } = useMediaQuery()
  const track = useAnalytics('segment')

  const { data } = useQueryGetInquiryById({ inquiryId })

  const inquiry = data?.inquiry

  const {
    handleApprove,
    handleOpenBooking,
    inquiryStatus,
    openRejectModal,
    handleReopen,
    userId,
  } = useInquiryActions({ inquiry })

  const isExpired = inquiryStatus === 'expired'
  const isRejected = inquiryStatus === 'rejected'
  const isRequested = inquiryStatus === 'requested'
  const isApproved = inquiryStatus === 'approved'

  const renderDropdown = () => {
    return (
      <Menu position='bottom-end'>
        <Menu.Target>
          <ActionIcon
            variant='default'
            data-testid='more-options'
            onClick={() => {
              track('LEAD__CLICK__MORE_ACTIONS', {
                lead_id: inquiry?.internal_id || '',
                owner_id: String(userId),
              })
            }}
          >
            <Icon icon={faEllipsisV} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Icon icon={faHistory} />}
            onClick={() => handleReopen()}
            disabled={isExpired || isRequested}
            data-testid='reopen-inquiry'
          >
            {t('reopen_request')}
          </Menu.Item>
          <Menu.Item
            leftSection={<Icon icon={faBan} />}
            onClick={() => openRejectModal({ inquiry })}
            disabled={isExpired || isRejected || isApproved}
            data-testid='reject-inquiry'
          >
            {t('reject_request')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    )
  }

  if (inquiry && inquiry.booking) {
    return (
      <Flex gap='sm'>
        <Button
          leftSection={<Icon icon={faLink} />}
          variant='default'
          onClick={handleOpenBooking}
          data-testid='open-booking'
        >
          {t('open-booking', {
            replace: { id: inquiry.booking.internal_id },
          })}
        </Button>
        {renderDropdown()}
      </Flex>
    )
  }

  if (inquiry && ['rejected', 'expired'].includes(inquiry.status)) {
    return <Box>{renderDropdown()}</Box>
  }

  return isMobile ? (
    <Menu position='bottom-end'>
      <Menu.Target>
        <Button>{tCommon('actions')}</Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<Icon icon={faPlus} />}
          onClick={handleApprove}
          data-testid='create-booking'
        >
          {t('create-booking')}
        </Menu.Item>
        <Menu.Item
          leftSection={<Icon icon={faBan} />}
          onClick={() => openRejectModal({ inquiry })}
          data-testid='reject-inquiry'
        >
          {t('reject_request')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <>
      <Button
        variant='default'
        leftSection={<Icon icon={faBan} />}
        onClick={() => openRejectModal({ inquiry })}
        data-testid='reject-inquiry'
      >
        {t('reject_request')}
      </Button>
      <Button
        leftSection={<Icon icon={faPlus} />}
        onClick={handleApprove}
        data-testid='create-booking'
      >
        {t('create-booking')}
      </Button>
    </>
  )
}
